import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';

// Define rewards interface
export interface Rewards {
  loyaltyPoints: number;
  dollarValue: string;
  discountValue: string;
}

// Define product interface
export interface Product {
  id: string;
  name: string;
  brand?: string;
  image: string;
  backDropImage?: string;
  category?: string;
  size: string;
  price?: number;
  rewardValue?: number;
  isBranded: boolean;
  rewards?: Rewards;
}

// Define reward method enum
export enum RewardMethod {
  LOYALTY_POINTS = 'loyalty_points',
  DIGITAL_CASH = 'digital_cash',
  DISCOUNT_COUPON = 'discount_coupon'
}

// Define empty item with quantity
export interface EmptyItem {
  product: Product;
  quantity: number;
}

// Define model types
export enum ModelType {
  DEV = 'dev',    // BARCODE-DEMO
  DEMO = 'demo'   // PRODUCT-IDENTIFICATION
}

// Define settings state
export interface Settings {
  testMode: boolean;
  inlineSignUp: boolean;
  inlineSignIn: boolean;
  adaGuidelines: boolean;
  selectedModel: ModelType;
  productReviewAndScan: boolean;
}

// Define kiosk state
export interface KioskState {
  isItemDispensed: boolean;
}

// Define the shape of our app state
interface AppState {
  currentStep: number;
  userEmail?: string;
  itemsCount: number;
  hasAcceptedTerms: boolean;
  isGuest: boolean;
  isMember: boolean;
  isSignup: boolean; // Flag to distinguish between login and signup flow
  hasClaimedOffer: boolean; // Flag to track if user claimed the offer
  selectedRewardMethod?: RewardMethod;
  empties: EmptyItem[];
  lastAddedProduct?: Product;
  settings: Settings;
  kiosk: KioskState;
  hasAcceptedUnidentifiedEmpty: boolean;
}

// Define action types
type AppAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_EMAIL'; payload: string }
  | { type: 'SET_ITEMS_COUNT'; payload: number }
  | { type: 'ACCEPT_TERMS' }
  | { type: 'SET_GUEST'; payload: boolean }
  | { type: 'SET_MEMBER'; payload: boolean }
  | { type: 'SET_IS_SIGNUP'; payload: boolean }
  | { type: 'SET_CLAIMED_OFFER'; payload: boolean }
  | { type: 'SET_REWARD_METHOD'; payload: RewardMethod | undefined }
  | { type: 'PREVIEW_PRODUCT'; payload: Product }
  | { type: 'ADD_EMPTY'; payload: Product }
  | { type: 'REMOVE_EMPTY'; payload: string }
  | { type: 'CLEAR_EMPTIES' }
  | { type: 'TOGGLE_TEST_MODE' }
  | { type: 'TOGGLE_INLINE_SIGN_UP' }
  | { type: 'TOGGLE_INLINE_SIGN_IN' }
  | { type: 'TOGGLE_ADA_GUIDELINES' }
  | { type: 'SET_SELECTED_MODEL'; payload: ModelType }
  | { type: 'TOGGLE_PRODUCT_REVIEW_AND_SCAN' }
  | { type: 'SET_ITEM_DISPENSED'; payload: boolean }
  | { type: 'ACCEPT_UNIDENTIFIED_EMPTY' }
  | { type: 'RESET' };

// Initial state
const initialState: AppState = {
  currentStep: 1,
  userEmail: undefined,
  itemsCount: 0,
  hasAcceptedTerms: false,
  isGuest: false,
  isMember: false,
  isSignup: false,
  hasClaimedOffer: false,
  selectedRewardMethod: undefined,
  empties: [],
  lastAddedProduct: undefined,
  settings: {
    testMode: false,
    inlineSignUp: false,
    inlineSignIn: false,
    adaGuidelines: false,
    selectedModel: ModelType.DEV,
    productReviewAndScan: false,
  },
  kiosk: {
    isItemDispensed: false,
  },
  hasAcceptedUnidentifiedEmpty: false,
};

// Reducer function
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_EMAIL':
      return { ...state, userEmail: action.payload };
    case 'SET_ITEMS_COUNT':
      return { ...state, itemsCount: action.payload };
    case 'ACCEPT_TERMS':
      return { ...state, hasAcceptedTerms: true };
    case 'SET_GUEST':
      return { 
        ...state, 
        isGuest: action.payload,
        isMember: action.payload ? false : state.isMember // If setting as guest, clear member status
      };
    case 'SET_MEMBER':
      return { 
        ...state, 
        isMember: action.payload,
        isGuest: action.payload ? false : state.isGuest // If setting as member, clear guest status
      };
    case 'SET_IS_SIGNUP':
      return { ...state, isSignup: action.payload };
    case 'SET_CLAIMED_OFFER':
      return { ...state, hasClaimedOffer: action.payload };
    case 'SET_REWARD_METHOD':
      return { ...state, selectedRewardMethod: action.payload };
    case 'PREVIEW_PRODUCT':
      return { 
        ...state, 
        lastAddedProduct: action.payload,
        hasAcceptedUnidentifiedEmpty: false // Reset acceptance for new item preview
      };
    case 'ADD_EMPTY': {
      const product = action.payload;
      const existingItem = state.empties.find(item => item.product.id === product.id);
      
      if (existingItem) {
        // If product already exists, increment quantity
        return {
          ...state,
          empties: state.empties.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
          itemsCount: state.itemsCount + 1,
          lastAddedProduct: product
        };
      } else {
        // If new product, add to empties
        return {
          ...state,
          empties: [...state.empties, { product, quantity: 1 }],
          itemsCount: state.itemsCount + 1,
          lastAddedProduct: product
        };
      }
    }
    case 'REMOVE_EMPTY': {
      const productId = action.payload;
      const existingItem = state.empties.find(item => item.product.id === productId);
      
      if (existingItem && existingItem.quantity > 1) {
        // If quantity > 1, decrement quantity
        return {
          ...state,
          empties: state.empties.map(item =>
            item.product.id === productId
              ? { ...item, quantity: item.quantity - 1 }
              : item
          ),
          itemsCount: state.itemsCount - 1
        };
      } else {
        // If quantity = 1, remove item completely
        return {
          ...state,
          empties: state.empties.filter(item => item.product.id !== productId),
          itemsCount: state.itemsCount - 1
        };
      }
    }
    case 'CLEAR_EMPTIES':
      return {
        ...state,
        empties: [],
        itemsCount: 0,
        lastAddedProduct: undefined
      };
    case 'TOGGLE_TEST_MODE':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          testMode: !state.settings.testMode 
        }
      };
    case 'TOGGLE_INLINE_SIGN_UP':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          inlineSignUp: !state.settings.inlineSignUp 
        }
      };
    case 'TOGGLE_INLINE_SIGN_IN':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          inlineSignIn: !state.settings.inlineSignIn 
        }
      };
    case 'TOGGLE_ADA_GUIDELINES':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          adaGuidelines: !state.settings.adaGuidelines 
        }
      };
    case 'TOGGLE_PRODUCT_REVIEW_AND_SCAN':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          productReviewAndScan: !state.settings.productReviewAndScan 
        }
      };
    case 'SET_SELECTED_MODEL':
      return { 
        ...state, 
        settings: { 
          ...state.settings, 
          selectedModel: action.payload 
        }
      };
    case 'SET_ITEM_DISPENSED':
      return {
        ...state,
        kiosk: {
          ...state.kiosk,
          isItemDispensed: action.payload
        }
      };
    case 'ACCEPT_UNIDENTIFIED_EMPTY':
      return {
        ...state,
        hasAcceptedUnidentifiedEmpty: true
      };
    case 'RESET':
      return {
        ...initialState,
        settings: state.settings // Preserve settings across resets
      };
    default:
      return state;
  }
};

// Context type
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // Load settings from localStorage on initialization
  const getInitialSettings = (): Settings => {
    try {
      const savedSettings = localStorage.getItem('kiosk-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return {
          testMode: parsed.testMode || false,
          inlineSignUp: parsed.inlineSignUp || false,
          inlineSignIn: parsed.inlineSignIn || false,
          adaGuidelines: parsed.adaGuidelines || false,
          selectedModel: parsed.selectedModel || ModelType.DEV,
          productReviewAndScan: parsed.productReviewAndScan || false,
        };
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
    return initialState.settings;
  };

  const [state, dispatch] = useReducer(appReducer, {
    ...initialState,
    settings: getInitialSettings(),
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('kiosk-settings', JSON.stringify(state.settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, [state.settings]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;